import { type FC, type ReactNode } from "react";
import { useInView } from "react-intersection-observer";

import { cn } from "@btnet/utilities-core";
import { useWebviewContext } from "@btnet/utilities-webview";

import { getUniqueId } from "@buildertrend/components";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import { isInIframe } from "utilities/url/url";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTIconCloseOutlined } from "commonComponents/btWrappers/BTIcon";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import {
    EntityShareButtonBDS,
    type ISharingConfig,
} from "commonComponents/entity/sharing/EntityShareButton/EntityShareButton";
import { FocusProvider } from "commonComponents/utilities/Focus/FocusProvider";

import "./BTModalLayout.less";

export interface IBTModalLayoutProps {
    title?: ReactNode;
    modalHeaderClassName?: string;
    subtitle?: ReactNode;
    sharing?: ISharingConfig;
    footerContent?: ReactNode;
    modalConfig?: IModalConfiguration;
    className?: string;
    /**
     * Controls whether the close button and click-outside-to-close mask are enabled.
     * When in a webview context, both the close button and mask are always hidden regardless of this value.
     * @default true
     */
    closable?: boolean;
    headerLogoAlign?: "center" | "left";
    children?: ReactNode;
    testId?: string;
    additionalHeaderContent?: ReactNode;
    headerBottom?: ReactNode;
    layoutRef?: React.RefObject<HTMLDivElement>;
    headerRef?: React.RefObject<HTMLDivElement>;
}

export const BTModalLayout: FC<IBTModalLayoutProps & ITrackingProp> = track({})((props) => {
    const webviewContext = useWebviewContext();
    // Set initialInView to true to avoid a flash on the modal https://github.com/thebuilder/react-intersection-observer/issues/499
    const { ref: topRef, inView: topInView } = useInView({ threshold: 1, initialInView: true });
    const { ref: bottomRef, inView: bottomInView } = useInView({
        threshold: 1,
        initialInView: true,
    });

    const content = (
        <div
            className={cn("BTModalLayout", props.className)}
            data-testid={props.testId ?? "modalLayout"}
            ref={props.layoutRef}
        >
            {props.title && (
                <BTModalHeader
                    {...props}
                    topInView={topInView}
                    className={cn(props.modalHeaderClassName)}
                />
            )}
            <div
                className="ModalContentContainer"
                data-testid={props.testId ? `${props.testId}-content` : "modalContent"}
            >
                <div ref={topRef} />
                {props.children}
                <div ref={bottomRef} />
            </div>
            {props.footerContent && (
                <div
                    className={cn("BTModalFooter", {
                        Unstuck: !bottomInView,
                    })}
                    data-testid={props.testId ? `${props.testId}-footer` : "modalFooter"}
                >
                    {props.footerContent}
                </div>
            )}
            {props.closable !== false && !webviewContext && props.modalConfig?.beforeClose && (
                <div
                    className="BTModalMask"
                    onClick={() => {
                        props.modalConfig?.beforeClose();
                        props.tracking?.trackEvent({
                            event: "ModalClose",
                            extraInfo: {
                                isFromClickOut: true,
                            },
                        });
                    }}
                />
            )}
        </div>
    );
    return isInIframe() ? <FocusProvider>{content}</FocusProvider> : content;
});

export const BTModalHeader: FC<IBTModalLayoutProps & { topInView?: boolean }> = ({
    className,
    closable = true,
    modalConfig,
    sharing,
    subtitle,
    headerLogoAlign = "center",
    title,
    testId,
    additionalHeaderContent,
    headerRef,
    headerBottom,
    topInView,
}) => {
    const webviewContext = useWebviewContext();
    return (
        <div
            className={cn("BTModalHeader", { Unstuck: !topInView })}
            data-testid={testId ? `${testId}-header` : "modalHeader"}
            ref={headerRef}
        >
            <div className={cn("Top", className)}>
                <div
                    className={cn("ModalHeaderContainer flex-grow-1", {
                        "ModalHeaderContainer--left": headerLogoAlign === "left",
                    })}
                >
                    <div className="ModalHeaderContent">
                        {subtitle && <div className="ModalHeaderJobName">{subtitle}</div>}
                        {title && (
                            <span className="ShareableTitle">
                                <BTTitle level={2} bold className="ModalHeaderTitle">
                                    {title}
                                </BTTitle>

                                <EntityShareButtonBDS
                                    id={`entityShareButtonBds${getUniqueId()}`}
                                    sharing={sharing}
                                />
                            </span>
                        )}
                    </div>
                </div>
                {additionalHeaderContent}

                {closable && !webviewContext && (
                    <BTButtonDeprecated
                        type="tertiary"
                        isolated
                        data-testid="close"
                        hotkey={{ hotkey: "close", popoverPlacement: "bottom" }}
                        onClick={modalConfig?.beforeClose}
                        className="ModalHeaderClose"
                        icon={
                            <BTIconCloseOutlined
                                title="Close"
                                size={20}
                                className="ModalHeaderCloseIcon"
                            />
                        }
                    />
                )}
            </div>
            {headerBottom && (
                <BTRow className="BTModalHeader-Bottom">
                    <BTCol span={24}>{headerBottom}</BTCol>
                </BTRow>
            )}
        </div>
    );
};
